import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateInventoryValuationPdfFilename } from './pdfFilename';

// ==================== TYPES ====================

interface MaterialValuationRow {
  name: string;
  sku: string;
  categoryName: string;
  totalOnHand: number;
  totalReserved: number;
  averageCost: number;
  totalValue: number;
  unit: string;
}

interface CategoryValuationRow {
  name: string;
  itemCount: number;
  totalValue: number;
  percentage: number;
}

interface ProjectCostRow {
  projectName: string;
  transactionCount: number;
  totalCost: number;
}

interface SummaryData {
  totalMaterialValue: number;
  activeItems: number;
  totalEquipmentValue: number;
  totalConsumedValue: number;
}

export interface InventoryValuationPdfOptions {
  summary: SummaryData;
  materialValuations: MaterialValuationRow[];
  categoryValuations: CategoryValuationRow[];
  projectCosts: ProjectCostRow[];
}

// ==================== COLORS ====================

const PRIMARY: [number, number, number] = [79, 70, 229]; // indigo-600
const HEADER_BG: [number, number, number] = [249, 250, 251]; // gray-50
const TEXT_PRIMARY: [number, number, number] = [17, 24, 39]; // gray-900
const TEXT_SECONDARY: [number, number, number] = [107, 114, 128]; // gray-500
const GREEN: [number, number, number] = [22, 163, 74]; // green-600
const AMBER: [number, number, number] = [217, 119, 6]; // amber-600
const RED: [number, number, number] = [220, 38, 38]; // red-600

// ==================== HELPERS ====================

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

// ==================== MAIN ====================

export function generateInventoryValuationPdf({
  summary,
  materialValuations,
  categoryValuations,
  projectCosts,
}: InventoryValuationPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Inventory Valuation Report', margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`As of ${today}`, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // SUMMARY STATS (4 cards)
  // ========================
  const statsY = 28;
  const cardGap = 4;
  const cardWidth = (pageWidth - margin * 2 - cardGap * 3) / 4;
  const cardHeight = 18;

  const stats = [
    { label: 'Total Inventory Value', value: formatCurrency(summary.totalMaterialValue), color: GREEN },
    { label: 'Total Items', value: formatNumber(summary.activeItems), color: PRIMARY },
    { label: 'Total Consumed Value', value: formatCurrency(summary.totalConsumedValue), color: RED },
    { label: 'Equipment Assets', value: formatCurrency(summary.totalEquipmentValue), color: AMBER },
  ];

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);

    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE 1: Valuation by Category
  // ========================
  let currentY = statsY + cardHeight + 8;

  doc.setTextColor(...TEXT_PRIMARY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Valuation by Category', margin, currentY + 5);
  currentY += 9;

  const categoryRows = categoryValuations.map((cat) => ({
    name: cat.name,
    items: String(cat.itemCount),
    totalValue: formatCurrency(cat.totalValue),
    percentage: `${cat.percentage.toFixed(1)}%`,
  }));

  autoTable(doc, {
    startY: currentY,
    columns: [
      { header: 'Category', dataKey: 'name' },
      { header: 'Items', dataKey: 'items' },
      { header: 'Total Value', dataKey: 'totalValue' },
      { header: '% of Total', dataKey: 'percentage' },
    ],
    body: categoryRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      items: { halign: 'right' },
      totalValue: { halign: 'right' },
      percentage: { halign: 'right' },
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    foot: [[
      { content: 'Total Materials', styles: { fontStyle: 'bold' } },
      { content: String(summary.activeItems), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: formatCurrency(summary.totalMaterialValue), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: '100%', styles: { halign: 'right', fontStyle: 'bold' } },
    ]],
    footStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_PRIMARY,
      fontSize: 8,
    },
    didDrawPage: () => drawFooter(doc, pageWidth, pageHeight, margin, today),
  });

  // ========================
  // TABLE 2: Detailed Inventory Breakdown
  // ========================
  const afterCategoryY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;

  // Check if we need a new page
  if (afterCategoryY > pageHeight - 40) {
    doc.addPage();
    currentY = 14;
  } else {
    currentY = afterCategoryY;
  }

  doc.setTextColor(...TEXT_PRIMARY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Detailed Inventory Breakdown', margin, currentY + 5);
  currentY += 9;

  const materialRows = materialValuations.map((m) => ({
    name: m.name,
    sku: m.sku || '-',
    category: m.categoryName || '-',
    onHand: `${formatNumber(m.totalOnHand)} ${m.unit || 'units'}`,
    reserved: m.totalReserved > 0 ? formatNumber(m.totalReserved) : '-',
    avgCost: formatCurrency(m.averageCost || 0),
    totalValue: formatCurrency(m.totalValue),
  }));

  autoTable(doc, {
    startY: currentY,
    columns: [
      { header: 'Material', dataKey: 'name' },
      { header: 'SKU', dataKey: 'sku' },
      { header: 'Category', dataKey: 'category' },
      { header: 'Qty On Hand', dataKey: 'onHand' },
      { header: 'Reserved', dataKey: 'reserved' },
      { header: 'Avg Unit Cost', dataKey: 'avgCost' },
      { header: 'Total Value', dataKey: 'totalValue' },
    ],
    body: materialRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      onHand: { halign: 'right' },
      reserved: { halign: 'right' },
      avgCost: { halign: 'right' },
      totalValue: { halign: 'right' },
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    didDrawPage: () => drawFooter(doc, pageWidth, pageHeight, margin, today),
  });

  // ========================
  // TABLE 3: Material Cost by Project (if any)
  // ========================
  if (projectCosts.length > 0) {
    const afterMaterialY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;

    if (afterMaterialY > pageHeight - 40) {
      doc.addPage();
      currentY = 14;
    } else {
      currentY = afterMaterialY;
    }

    doc.setTextColor(...TEXT_PRIMARY);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Material Cost by Project', margin, currentY + 5);
    currentY += 9;

    const projectRows = projectCosts.map((pc) => ({
      project: pc.projectName,
      draws: String(pc.transactionCount),
      totalCost: formatCurrency(pc.totalCost),
    }));

    const totalDraws = projectCosts.reduce((s, pc) => s + pc.transactionCount, 0);
    const totalCost = projectCosts.reduce((s, pc) => s + pc.totalCost, 0);

    autoTable(doc, {
      startY: currentY,
      columns: [
        { header: 'Project', dataKey: 'project' },
        { header: 'Draws', dataKey: 'draws' },
        { header: 'Total Material Cost', dataKey: 'totalCost' },
      ],
      body: projectRows,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        textColor: TEXT_PRIMARY,
        lineColor: [229, 231, 235],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: HEADER_BG,
        textColor: TEXT_SECONDARY,
        fontStyle: 'bold',
        fontSize: 7,
        halign: 'left',
      },
      columnStyles: {
        draws: { halign: 'right' },
        totalCost: { halign: 'right' },
      },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      foot: [[
        { content: 'Total', styles: { fontStyle: 'bold' } },
        { content: String(totalDraws), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalCost), styles: { halign: 'right', fontStyle: 'bold' } },
      ]],
      footStyles: {
        fillColor: HEADER_BG,
        textColor: TEXT_PRIMARY,
        fontSize: 8,
      },
      didDrawPage: () => drawFooter(doc, pageWidth, pageHeight, margin, today),
    });
  }

  // ========================
  // PAGE NUMBERS
  // ========================
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  const filename = generateInventoryValuationPdfFilename();
  doc.save(filename);
}

// ========================
// FOOTER HELPER
// ========================
function drawFooter(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  margin: number,
  today: string,
) {
  const pageNumber = doc.getCurrentPageInfo().pageNumber;

  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

  doc.setFontSize(7);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on ${today}`, margin, pageHeight - 7);
  doc.text(
    `Page ${pageNumber} of {total_pages}`,
    pageWidth - margin,
    pageHeight - 7,
    { align: 'right' },
  );
}
